import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useSnackBar from '../../../../hooks/useSnackBar/useSnackBar';
import useHttpError from '../../../../hooks/useHttpError/useHttpError';

import { NotificationSetting } from '../../../../models/CompanyOnboardingModels';

import CompanyOnboardingService from '../../../../services/CompanyOnboarding/CompanyOnboardingService';
import UsersService from '../../../../services/Users/UsersService';

import { RootState } from '../../../../stores/Store';
import { setNotificationSettings } from '../../../../stores/AssignDeviceStore';

function useAssignUser() {
  const snackBar = useSnackBar();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { getHttpReason } = useHttpError();
  const [isLoading, setIsLoading] = useState(false);
  const { request, deviceToAssign, qrCodeNotificationSettings } = useSelector(
    (state: RootState) => state.assignDeviceReducer,
  );

  /**
   * Assign user request
   * @param request
   */
  const assignUser = (onClose: () => void) => {
    setIsLoading(true);
    UsersService.api
      .assignDevice(request)
      .then(
        () => {
          snackBar.showSnackBar(t('pages.devicesReport.assignUserDrawer.assignUserSuccess'), 'success');
          onClose();
        },
        (res) => {
          const error = getHttpReason(res.response.data, t('pages.devicesReport.assignUserDrawer.assignUserError'));
          snackBar.showSnackBar(error, 'error');
        },
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Retrieves only the types of notification that are enabled
   * @param notifications
   */
  const filterEnabledQrCodeNotifications = (notifications: NotificationSetting[]): NotificationSetting[] =>
    notifications.filter((notification) => notification.enabled);

  /**
   * Fetches the list of notification settings of the company
   * @param companyId
   */
  const getNotificationSettings = (companyId: string) => {
    CompanyOnboardingService.api.fetchCompanyNotificationSettings(companyId).then(
      (res) => {
        dispatch(setNotificationSettings(filterEnabledQrCodeNotifications(res.data.qrCodeNotifications)));
      },
      () => {
        snackBar.showSnackBar(t('pages.devicesReport.assignUserDrawer.fetchCompanyNotificationsDefaultError'), 'error');
      },
    );
  };

  useEffect(() => {
    if (deviceToAssign?.companyId && !qrCodeNotificationSettings.length) {
      getNotificationSettings(deviceToAssign.companyId);
    }
  }, [deviceToAssign]);

  return { assignUser, isLoading };
}

export default useAssignUser;
